import React from 'react'
import { Layout, Typography, Row, Col, Card } from 'antd'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { siteSettingsApi, aboutContentApi } from '../../services/api'

const { Content } = Layout
const { Title, Paragraph } = Typography

const AboutPage: React.FC = () => {
  const { i18n, t } = useTranslation()
  const currentLang = i18n.language as 'en' | 'zh'

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Fetch about content
  const { data: aboutContentData } = useQuery({
    queryKey: ['publicAboutContent'],
    queryFn: aboutContentApi.getPublicContent,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const settings = siteSettingsData?.settings
  const aboutContent = aboutContentData?.content

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }
  

  return (
    <>
      <Helmet>
        <title>{t('about.title')} - {getLocalizedText(settings?.siteName, 'MedCareHub')}</title>
        <meta name="description" content={`Learn about ${getLocalizedText(settings?.siteName, 'our company')}'s healthcare innovation and commitment to improving lives through advanced medical technology.`} />
      </Helmet>

      <Content>
        {/* Hero Section: image or video (locale-aware) */}
        <section className="relative flex items-start justify-center pt-20 h-[50vh] text-white">
          {(() => {
            const banner = settings?.pages?.about?.banner?.[currentLang];
            if (banner?.mediaType === 'video' && banner?.videoUrl) {
              return (
                <video className="absolute inset-0 w-full h-full object-cover" src={banner.videoUrl} autoPlay muted loop playsInline />
              );
            }
            const bgUrl = banner?.imageUrl || settings?.pages?.about?.bannerImage || '/aboutusbanner.jpg';
            return (
              <div className="absolute inset-0 bg-center bg-no-repeat" style={{ backgroundImage: `url('${bgUrl}')`, backgroundSize: '100% auto' }} />
            );
          })()}
          <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
            <Title level={1} className="text-4xl font-bold text-white mb-4 drop-shadow-lg" style={{ color: '#ffffff' }}>
              {getLocalizedText(settings?.pages?.about?.title) || t('about.title')}
            </Title>
            <Paragraph className="text-xl text-white max-w-3xl mx-auto drop-shadow-lg">
              {getLocalizedText(settings?.pages?.about?.subtitle) || t('about.subtitle')}
            </Paragraph>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <Row gutter={[32, 32]} align="middle">
              <Col xs={24} lg={12}>
                <Title level={2} className="text-3xl font-bold text-gray-900 mb-6">
                  {getLocalizedText(aboutContent?.overview?.title)}
                </Title>
                <Paragraph className="text-lg text-gray-600" style={{ whiteSpace: 'pre-wrap' }}>
                  {getLocalizedText(aboutContent?.overview?.content)}
                </Paragraph>
              </Col>
              <Col xs={24} lg={12}>
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <div className="aspect-video">
                    {(() => {
                      const mediaType = getLocalizedText(aboutContent?.overview?.mediaType);
                      const mediaUrl = getLocalizedText(aboutContent?.overview?.mediaUrl);
                      
                      console.log('=== MEDIA DEBUG ===');
                      console.log('mediaType:', mediaType);
                      console.log('mediaUrl:', mediaUrl);
                      console.log('aboutContent?.overview:', aboutContent?.overview);
                      
                      // Simple check: if we have a custom URL that's not the default
                      const isCustomVideo = mediaUrl && 
                        mediaUrl !== '/aboutusvideo.mp4' && 
                        !mediaUrl.includes('/aboutusvideo.mp4');
                      
                      const isCustomImage = mediaUrl && 
                        mediaUrl !== '/aboutusbanner.jpg' && 
                        !mediaUrl.includes('/aboutusbanner.jpg');
                      
                      console.log('isCustomVideo:', isCustomVideo);
                      console.log('isCustomImage:', isCustomImage);
                      
                      if (mediaType === 'image') {
                        const imageSrc = isCustomImage ? mediaUrl : '/aboutusbanner.jpg';
                        console.log('Rendering image with src:', imageSrc);
                        return (
                          <img 
                            key={`image-${imageSrc}`}
                            src={imageSrc} 
                            alt="About Us" 
                            className="w-full h-full object-cover"
                          />
                        );
                      } else {
                        const videoSrc = isCustomVideo ? mediaUrl : '/aboutusvideo.mp4';
                        console.log('Rendering video with src:', videoSrc);
                        console.log('Video key:', `video-${videoSrc}`);
                        
                        return (
                          <video 
                            key={`video-${videoSrc}`}
                            className="w-full h-full object-cover"
                            autoPlay
                            muted
                            loop
                            playsInline
                            onLoadStart={() => console.log('Video load started:', videoSrc)}
                            onCanPlay={() => console.log('Video can play:', videoSrc)}
                            onError={(e) => console.log('Video error:', e, 'for src:', videoSrc)}
                          >
                            <source src={videoSrc} type="video/mp4" />
                            <div className="bg-gray-100 w-full h-full flex items-center justify-center">
                              <span className="text-gray-500">Your browser does not support the video tag.</span>
                            </div>
                          </video>
                        );
                      }
                    })()}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </section>



        {/* Our Values */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                {getLocalizedText(aboutContent?.valuesSection?.title) || 'Our Values'}
              </Title>
              <Paragraph className="text-lg text-gray-600">
                {getLocalizedText(aboutContent?.valuesSection?.subtitle) || 'To improve lives and contribute to a better society'}
              </Paragraph>
            </div>
            
            <Row gutter={[24, 24]}>
              {aboutContent?.values && aboutContent.values.length > 0 ? (
                aboutContent.values.map((value: any) => (
                  <Col xs={24} md={8} key={value.id}>
                    <Card className="h-full text-center">
                      <Title level={4} className="text-gray-900 mb-3 font-semibold">
                        {getLocalizedText(value.title)}
                      </Title>
                      <Paragraph className="text-gray-700">
                        {getLocalizedText(value.description)}
                      </Paragraph>
                    </Card>
                  </Col>
                ))
              ) : (
                // Fallback to default values if no API data
                <>
                  <Col xs={24} md={8}>
                    <Card className="h-full text-center">
                      <Title level={4} className="text-gray-900 mb-3 font-semibold">
                        Innovation Driven by Social Needs
                      </Title>
                      <Paragraph className="text-gray-700">
                        Be a pioneer in creating inspired solutions for the future.
                      </Paragraph>
                    </Card>
                  </Col>
                  
                  <Col xs={24} md={8}>
                    <Card className="h-full text-center">
                      <Title level={4} className="text-gray-900 mb-3 font-semibold">
                        Challenging Ourselves
                      </Title>
                      <Paragraph className="text-gray-700">
                        Pursue new challenges with passion and courage.
                      </Paragraph>
                    </Card>
                  </Col>
                  
                  <Col xs={24} md={8}>
                    <Card className="h-full text-center">
                      <Title level={4} className="text-gray-900 mb-3 font-semibold">
                        Respect for All
                      </Title>
                      <Paragraph className="text-gray-700">
                        Act with integrity and encourage everyone's potential.
                      </Paragraph>
                    </Card>
                  </Col>
                </>
              )}
            </Row>
          </div>
        </section>
      </Content>
    </>
  )
}

export default AboutPage